import { devLog } from '../../utils/devLog';

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { getRoleQuestions } from '../../utils/api';
import EmptyState from '../common/EmptyState';
import QuestionItem from './QuestionItem';

const QuestionList = () => {
  const { jobs } = useAppContext();
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadQuestions = useCallback(async () => {
    if (!selectedRoleId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getRoleQuestions(selectedRoleId);
      if (result.success) {
        // Transform API response to match expected format
        const formattedQuestions = result.data.questions.map(q => ({
          id: q.id || q._id,
          text: q.text,
          type: q.type,
          time: q.time,
          weight: q.weight,
          jobId: selectedRoleId,
          createdAt: q.createdAt
        }));
        setQuestions(formattedQuestions);
      } else {
        setError(result.error || 'Failed to load questions');
        setQuestions([]);
      }
    } catch (err) {
      devLog.error('Error loading questions:', err);
      setError('Failed to load questions. Please try again.');
      setQuestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedRoleId]);

  // Fetch questions when role is selected
  useEffect(() => {
    if (selectedRoleId) {
      loadQuestions();
    } else {
      setQuestions([]);
    }
  }, [selectedRoleId, loadQuestions]);

  // Filter to show only Open (active) jobs
  const openJobs = jobs.filter(job => job.status === 'Open');

  return (
    <div style={{ marginTop: '30px' }}>
      <h3 style={{ color: '#4a5568', marginBottom: '15px' }}>Question Bank</h3>
      
      {/* Role Selector */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '20px' }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Select Job Position to View Questions</label>
          <select 
            className="form-input"
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
          >
            <option value="">-- Select a job position --</option>
            {openJobs.length === 0 ? (
              <option value="" disabled>No active job positions available</option>
            ) : (
              openJobs.map(job => (
                <option key={job.id} value={job.roleId || job.id}>{job.title}</option>
              ))
            )}
          </select>
        </div>
        {selectedRoleId && (
          <button
            onClick={loadQuestions}
            disabled={isLoading}
            className="button button-secondary"
            style={{ 
              padding: '10px 20px',
              whiteSpace: 'nowrap',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
            title="Refresh questions list"
          >
            {isLoading ? '⏳' : '🔄'} Refresh
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
          <p style={{ color: '#718096' }}>Loading questions...</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div style={{ 
          padding: '15px', 
          background: '#fee2e2', 
          color: '#dc2626', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Questions List */}
      {!isLoading && !error && selectedRoleId && questions.length === 0 && (
        <EmptyState 
          message="No questions added yet for this role. Add questions using the form above." 
          icon="❓" 
        />
      )}

      {!isLoading && !error && selectedRoleId && questions.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 500 }}>
              Showing {questions.length} question{questions.length !== 1 ? 's' : ''} for this role
            </div>
            {questions.length < 5 ? (
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                color: '#856404',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚠️ {questions.length}/5 questions — Add at least {5 - questions.length} more to enable HR Interview mode
              </div>
            ) : (
              <div style={{
                background: '#d1fae5',
                border: '1px solid #6ee7b7',
                color: '#065f46',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ✅ {questions.length} questions — HR Interview mode ready
              </div>
            )}
          </div>
          {questions.map((question, index) => (
            <QuestionItem 
              key={question.id} 
              question={question} 
              index={index}
              onDelete={loadQuestions} // Reload questions after deletion
            />
          ))}
        </div>
      )}

      {!selectedRoleId && !isLoading && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#718096',
          fontStyle: 'italic'
        }}>
          👆 Select a job position above to view its interview questions
        </div>
      )}
    </div>
  );
};

export default QuestionList;
